/**
 * Canvas Renderer for Hybrid Rendering System
 * Handles static element rendering to Canvas layer for performance optimization
 */

import type { CanvasElement, CanvasViewport, DirtyRegion, TransformState, SpatialIndex } from './types'
import { buildSpatialItems, createSpatialIndex, queryVisibleElements, intersectsViewport } from './pathCache'
import {
  renderElementToContext,
  groupElementsIntoBatches,
  renderBatch,
  filterElementsByDirtyRegions,
  isPointInElement
} from './renderingEngine'
import {
  calculateDirtyRegions,
  createFullCanvasDirtyRegion,
  transformRegionToCanvas,
  transformPointToWorld
} from './transformManager'

export class CanvasRenderer {
  private canvas: HTMLCanvasElement
  private ctx: CanvasRenderingContext2D
  private elements: Map<string, CanvasElement> = new Map()
  private cache: Map<string, ImageData> = new Map()
  private pathCache: Map<string, Path2D> = new Map()
  private sortedElementsCache: CanvasElement[] = []
  private viewport: CanvasViewport = { x: 0, y: 0, width: 0, height: 0, scale: 1 }
  private isDirty = true
  private currentTransform: TransformState = { scale: 1, translateX: 0, translateY: 0 }

  // Dirty region tracking
  private dirtyRegions: DirtyRegion[] = []
  private previousViewport: CanvasViewport = { x: 0, y: 0, width: 0, height: 0, scale: 1 }
  private useDirtyRegions = true

  // Virtual viewport with offscreen canvas
  private offscreenCanvas: OffscreenCanvas | null = null
  private offscreenCtx: OffscreenCanvasRenderingContext2D | null = null
  private useOffscreenCanvas = true

  // Spatial indexing with R-Tree
  private spatialIndex: SpatialIndex
  private useSpatialIndex = true

  // Progressive rendering
  private renderFrame = 0
  private maxElementsPerFrame = 100
  private pendingRenderElements: CanvasElement[] = []
  private renderAnimationFrame: number | null = null

  // Batch drawing optimization
  private useBatchDrawing = true

  constructor(width: number = 10000, height: number = 10000) {
    this.canvas = document.createElement('canvas')
    this.canvas.width = width
    this.canvas.height = height
    this.canvas.className = 'canvas-layer'
    this.canvas.style.position = 'absolute'
    this.canvas.style.top = '0'
    this.canvas.style.left = '0'
    this.canvas.style.pointerEvents = 'none'

    const context = this.canvas.getContext('2d', {
      alpha: true,
      desynchronized: true,
      willReadFrequently: false
    })

    if (!context) {
      throw new Error('Failed to get 2D context from canvas')
    }

    this.ctx = context
    this.ctx.imageSmoothingEnabled = true
    this.ctx.imageSmoothingQuality = 'high'

    // Initialize spatial index
    this.spatialIndex = createSpatialIndex()

    // Initialize offscreen canvas if supported
    if (typeof OffscreenCanvas !== 'undefined' && this.useOffscreenCanvas) {
      try {
        this.offscreenCanvas = new OffscreenCanvas(width, height)
        const offscreenContext = this.offscreenCanvas.getContext('2d', {
          alpha: true,
          desynchronized: true
        })

        if (offscreenContext) {
          this.offscreenCtx = offscreenContext as OffscreenCanvasRenderingContext2D
          this.offscreenCtx.imageSmoothingEnabled = true
          this.offscreenCtx.imageSmoothingQuality = 'high'
        } else {
          this.useOffscreenCanvas = false
        }
      } catch (error) {
        this.useOffscreenCanvas = false
      }
    }
  }

  /**
   * Get the canvas element for DOM insertion
   */
  getCanvas(): HTMLCanvasElement {
    return this.canvas
  }

  /**
   * Add elements to be rendered
   */
  addElements(elements: CanvasElement[]): void {
    for (const element of elements) {
      this.elements.set(element.id, element)
    }

    // Bulk load into spatial index
    if (this.useSpatialIndex && elements.length > 0) {
      const spatialItems = buildSpatialItems(elements)
      this.spatialIndex.load(spatialItems)
    }

    this.sortedElementsCache = []
    this.isDirty = true
  }

  /**
   * Clear all elements
   */
  clearElements(): void {
    this.elements.clear()
    this.cache.clear()
    this.pathCache.clear()
    this.sortedElementsCache = []
    this.isDirty = true
    this.clear()

    if (this.useSpatialIndex) {
      this.spatialIndex.clear()
    }

    if (this.renderAnimationFrame !== null) {
      cancelAnimationFrame(this.renderAnimationFrame)
      this.renderAnimationFrame = null
    }
    this.pendingRenderElements = []
  }

  /**
   * Update viewport for culling
   */
  setViewport(viewport: CanvasViewport): void {
    if (this.useDirtyRegions) {
      const regions = calculateDirtyRegions(this.previousViewport, viewport)
      if (regions.length > 0) {
        this.dirtyRegions = regions
      } else if (this.previousViewport.scale !== viewport.scale) {
        // Scale changed - mark entire canvas dirty
        this.markEntireCanvasDirty()
      }
      this.previousViewport = { ...viewport }
    }

    this.viewport = viewport
    this.isDirty = true
  }

  /**
   * Apply transform to match JointJS paper
   */
  applyTransform(scale: number, translateX: number, translateY: number): void {
    this.currentTransform = { scale, translateX, translateY }
    this.viewport.scale = scale
    this.viewport.x = translateX
    this.viewport.y = translateY
    this.isDirty = true
  }

  /**
   * Render all static elements to canvas
   */
  render(forceRedraw: boolean = false): void {
    if (!this.isDirty && !forceRedraw) {
      return
    }

    const visibleElements = this.cullElements()
    const useProgressive = visibleElements.length > this.maxElementsPerFrame * 2

    if (useProgressive && !forceRedraw) {
      this.renderProgressive(visibleElements)
      return
    }

    this.renderImmediate(visibleElements, forceRedraw)
  }

  /**
   * Hit detection for canvas elements
   */
  hitTest(x: number, y: number): CanvasElement | null {
    const point = transformPointToWorld(x, y, this.viewport)

    // Narrow to elements whose bounds contain the point via the spatial index
    // before sorting, so hover/click hit-testing is O(k log k) over a handful of
    // candidates instead of O(n log n) over every element on each pointermove.
    const candidates = this.getHitCandidates(point.x, point.y)

    for (const element of candidates) {
      if (isPointInElement(point.x, point.y, element, this.ctx, this.pathCache)) {
        return element
      }
    }

    return null
  }

  private getHitCandidates(worldX: number, worldY: number): CanvasElement[] {
    let candidates: CanvasElement[]
    if (this.useSpatialIndex && this.spatialIndex.all().length > 0) {
      candidates = queryVisibleElements(this.spatialIndex, {
        x: worldX - 0.5,
        y: worldY - 0.5,
        width: 1,
        height: 1
      })
    } else {
      candidates = Array.from(this.elements.values())
    }

    // Top-most first (descending z-order) so overlapping elements return the front one
    return candidates.sort((a, b) => b.zIndex - a.zIndex)
  }

  /**
   * Cache current canvas state
   */
  cacheCurrentState(key: string): void {
    const imageData = this.ctx.getImageData(0, 0, this.canvas.width, this.canvas.height)
    this.cache.set(key, imageData)
  }

  /**
   * Restore cached canvas state
   */
  restoreCachedState(key: string): boolean {
    const imageData = this.cache.get(key)
    if (!imageData) {
      return false
    }

    this.ctx.putImageData(imageData, 0, 0)
    this.isDirty = false
    return true
  }

  /**
   * Destroy and clean up
   */
  destroy(): void {
    this.elements.clear()
    this.cache.clear()
    this.pathCache.clear()
    this.clear()

    if (this.canvas.parentNode) {
      this.canvas.parentNode.removeChild(this.canvas)
    }
  }

  // --- Private methods ---

  private markEntireCanvasDirty(): void {
    this.dirtyRegions = [createFullCanvasDirtyRegion(this.canvas.width, this.canvas.height)]
    this.isDirty = true
  }

  private renderImmediate(visibleElements: CanvasElement[], forceRedraw: boolean): void {
    const startTime = performance.now()

    const renderCtx = this.useOffscreenCanvas && this.offscreenCtx ? this.offscreenCtx : this.ctx

    if (this.useDirtyRegions && this.dirtyRegions.length > 0 && !forceRedraw) {
      this.clearDirtyRegions()
    } else {
      this.clear()
      if (this.useDirtyRegions) {
        this.markEntireCanvasDirty()
      }
    }

    renderCtx.setTransform(1, 0, 0, 1, 0, 0)
    renderCtx.translate(this.currentTransform.translateX, this.currentTransform.translateY)
    renderCtx.scale(this.currentTransform.scale, this.currentTransform.scale)

    let elementsToRender: CanvasElement[]
    if (this.useDirtyRegions && this.dirtyRegions.length > 0) {
      elementsToRender = filterElementsByDirtyRegions(visibleElements, this.dirtyRegions)
    } else {
      elementsToRender = visibleElements
    }

    let sortedElements: CanvasElement[]
    if (this.sortedElementsCache.length === 0) {
      sortedElements = Array.from(elementsToRender).sort((a, b) => a.zIndex - b.zIndex)
      this.sortedElementsCache = sortedElements
    } else {
      sortedElements = this.sortedElementsCache.filter(el => elementsToRender.includes(el))
    }

    let rendered = 0

    if (this.useBatchDrawing && sortedElements.length > 10) {
      const batches = groupElementsIntoBatches(sortedElements)

      for (const [batchKey, batch] of batches) {
        try {
          renderBatch(batch, renderCtx, this.pathCache)
          rendered += batch.length
        } catch (error) {
        }
      }
    } else {
      for (const element of sortedElements) {
        try {
          renderElementToContext(element, renderCtx, this.pathCache)
          rendered++
        } catch (error) {
        }
      }
    }

    this.blitOffscreenToMain(renderCtx)

    this.dirtyRegions = []

    const renderTime = performance.now() - startTime

    if (rendered > 100) {
      const mode = `${this.useDirtyRegions ? 'Dirty' : 'Full'} ${this.useOffscreenCanvas ? 'Offscreen' : 'Main'}`
    }

    this.isDirty = false
  }

  private renderProgressive(visibleElements: CanvasElement[]): void {
    if (this.renderAnimationFrame !== null) {
      cancelAnimationFrame(this.renderAnimationFrame)
    }

    const sortedElements = Array.from(visibleElements).sort((a, b) => a.zIndex - b.zIndex)
    this.pendingRenderElements = sortedElements
    this.renderFrame = 0

    this.clear()
    // Full repaint: reset the offscreen buffer so the per-batch blits below show a
    // clean progressive build-up rather than stale pixels from a previous render.
    this.clearOffscreen()

    const renderCtx = this.useOffscreenCanvas && this.offscreenCtx ? this.offscreenCtx : this.ctx
    renderCtx.setTransform(1, 0, 0, 1, 0, 0)
    renderCtx.translate(this.currentTransform.translateX, this.currentTransform.translateY)
    renderCtx.scale(this.currentTransform.scale, this.currentTransform.scale)

    const renderNextBatch = () => {
      const startIdx = this.renderFrame * this.maxElementsPerFrame
      const endIdx = Math.min(startIdx + this.maxElementsPerFrame, this.pendingRenderElements.length)

      for (let i = startIdx; i < endIdx; i++) {
        try {
          renderElementToContext(this.pendingRenderElements[i], renderCtx, this.pathCache)
        } catch (error) {
        }
      }

      this.renderFrame++

      // Copy the accumulated offscreen buffer to the visible canvas after every
      // batch so elements appear progressively instead of the user staring at a
      // blank canvas until the whole frame finishes.
      this.blitOffscreenToMain(renderCtx)

      if (endIdx < this.pendingRenderElements.length) {
        this.renderAnimationFrame = requestAnimationFrame(renderNextBatch)
      } else {
        this.renderAnimationFrame = null
        this.pendingRenderElements = []
        this.isDirty = false
      }
    }

    renderNextBatch()
  }

  /**
   * Copy the offscreen buffer onto the visible canvas (no-op when offscreen is unused)
   */
  private blitOffscreenToMain(
    renderCtx: CanvasRenderingContext2D | OffscreenCanvasRenderingContext2D
  ): void {
    if (this.useOffscreenCanvas && this.offscreenCanvas && renderCtx === this.offscreenCtx) {
      this.ctx.clearRect(0, 0, this.canvas.width, this.canvas.height)
      this.ctx.drawImage(this.offscreenCanvas, 0, 0)
    }
  }

  /**
   * Clear the offscreen buffer (used before a full repaint)
   */
  private clearOffscreen(): void {
    if (this.offscreenCtx && this.offscreenCanvas) {
      this.offscreenCtx.setTransform(1, 0, 0, 1, 0, 0)
      this.offscreenCtx.clearRect(0, 0, this.offscreenCanvas.width, this.offscreenCanvas.height)
    }
  }

  private cullElements(): CanvasElement[] {
    if (!this.viewport || this.viewport.width === 0) {
      return Array.from(this.elements.values())
    }

    const padding = 500
    const expandedViewport = {
      x: this.viewport.x - padding,
      y: this.viewport.y - padding,
      width: this.viewport.width + padding * 2,
      height: this.viewport.height + padding * 2
    }

    if (this.useSpatialIndex && this.spatialIndex.all().length > 0) {
      return queryVisibleElements(this.spatialIndex, expandedViewport)
    }

    return Array.from(this.elements.values()).filter(element =>
      intersectsViewport(element.bounds, expandedViewport)
    )
  }

  private clear(): void {
    this.ctx.save()
    this.ctx.setTransform(1, 0, 0, 1, 0, 0)
    this.ctx.clearRect(0, 0, this.canvas.width, this.canvas.height)
    this.ctx.restore()
  }

  private clearDirtyRegions(): void {
    if (this.dirtyRegions.length === 0) return

    this.ctx.save()
    this.ctx.setTransform(1, 0, 0, 1, 0, 0)

    for (const region of this.dirtyRegions) {
      const canvasRegion = transformRegionToCanvas(region, this.currentTransform)
      this.ctx.clearRect(
        canvasRegion.x,
        canvasRegion.y,
        canvasRegion.width,
        canvasRegion.height
      )
    }

    this.ctx.restore()
  }
}
